import {
  Typography,
  Box,
  CircularProgress,
  Card,
  CardContent,
  Paper,
  Grid2,
  TextField,
  InputAdornment,
  ToggleButtonGroup,
  ToggleButton,
  Link,
} from "@mui/material";
import { Image, Search } from "@mui/icons-material";
import {
  Job,
  colorByStatus,
  statusToLabel,
  BrandBatchSettings,
} from "../../../pages/playground/types";
import { keywordsSearch } from "../../../api/phrasebook";
import { useEffect, useRef, useState } from "react";
import { getPhraseSections } from "../../../utils/fieldNames";
import FormattedText from "../../common/formatted-text";

export default function KeywordSearch({
  batchId,
  batchSettings,
}: {
  batchId: string;
  batchSettings?: BrandBatchSettings;
}) {
  const [jobsLoading, setJobsLoading] = useState<boolean>(false);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [query, setQuery] = useState<string>("");
  const [scope, setScope] = useState<"batch" | "brand">("batch");

  const abortControllerRef = useRef<AbortController | null>(null);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Clear any existing timeouts
    if (searchTimeout.current) {
      clearTimeout(searchTimeout.current);
    }

    // Abort any ongoing requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Don't search if query is empty
    if (!query.trim()) {
      setJobs([]);
      setJobsLoading(false);
      return;
    }

    // Create a new abort controller for this request
    abortControllerRef.current = new AbortController();

    // Keep showing existing jobs but limit them during loading for better UX
    setJobsLoading(true);
    if (Array.isArray(jobs)) {
      setJobs([...jobs.slice(0, 5)]);
    }

    // Add debounce to avoid excessive requests during typing
    searchTimeout.current = setTimeout(() => {
      keywordsSearch({
        batchId,
        query,
        scope,
        signal: abortControllerRef.current?.signal,
      })
        .then((response) => {
          const data = response.data.data;
          if (Array.isArray(data)) {
            setJobs(data);
          } else {
            console.error("Expected array but got:", data);
            setJobs([]);
          }
        })
        .catch((e) => {
          // Don't show errors for aborted requests
          if (e.name !== "AbortError" && e.name !== "CanceledError") {
            console.error("Error searching phrases by keywords:", e);
          }
          setJobs([]);
        })
        .finally(() => {
          setJobsLoading(false);
        });
    }, 300); // 300ms debounce

    // Cleanup function to abort request when component unmounts or dependencies change
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [query, batchId, scope]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        p: 2,
        height: "100%",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
          mb: 3,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="medium"
          label="Search by keywords"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          placeholder="e.g. Men's shirt"
        />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Search in:
          </Typography>
          <ToggleButtonGroup
            value={scope}
            exclusive
            onChange={(_, newScope) => {
              if (newScope !== null) {
                setScope(newScope);
              }
            }}
            size="small"
          >
            <ToggleButton value="batch">This Batch</ToggleButton>
            <ToggleButton value="brand">Brand</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="caption" color="text.secondary" sx={{ mt: -0.5 }}>
          Search for jobs containing these keywords in their phrases
        </Typography>
      </Box>

      {jobsLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {!jobsLoading && query && jobs.length === 0 && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 4 }}
        >
          No jobs found {scope === "batch" ? "in this batch" : "in this brand"}{" "}
          for keywords "{query}".
        </Typography>
      )}

      {!jobsLoading && !query && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 4 }}
        >
          Enter keywords to search for similar jobs
        </Typography>
      )}

      {!jobsLoading && jobs.length > 0 && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Found {jobs.length} job{jobs.length !== 1 ? "s" : ""} matching "
            {query}"
          </Typography>
          <Grid2 container spacing={2}>
            {jobs.map((job) => {
              // Get phrase sections with custom labels
              const phraseSections = getPhraseSections(batchSettings);

              // Build sections with content
              const sectionsWithContent = phraseSections.map((section) => ({
                ...section,
                content: job.phrases.filter(
                  (phrase) => phrase.type === section.type
                ),
              }));

              const keywordParams = new URLSearchParams();
              keywordParams.set("keyword_query", query);
              keywordParams.set("keyword_scope", scope);
              keywordParams.set("keyword_batch_id", batchId);
              const jobUrl = `/job/${job.id}?${keywordParams.toString()}`;

              return (
                <Grid2 key={job.id} size={6}>
                  <Link
                    href={jobUrl}
                    underline="none"
                    color="inherit"
                    sx={{ display: "block", height: "100%" }}
                  >
                    <Card
                      variant="outlined"
                      sx={{
                        height: "100%",
                        cursor: "pointer",
                        transition: "box-shadow 0.2s",
                        "&:hover": {
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 1,
                          }}
                        >
                          <Box
                            component="span"
                            sx={{
                              backgroundColor: "grey.100",
                              color: "text.primary",
                              fontSize: "0.8rem",
                              py: 0.3,
                              px: 0.8,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              fontWeight: 600,
                            }}
                          >
                            {job.master_id}
                          </Box>

                          {job.garment_type && (
                            <Box
                              component="span"
                              sx={{
                                backgroundColor: "grey.100",
                                color: "text.primary",
                                fontSize: "0.75rem",
                                py: 0.3,
                                px: 0.8,
                                borderRadius: 1,
                                display: "flex",
                                alignItems: "center",
                                fontWeight: 500,
                              }}
                            >
                              {job.garment_type}
                            </Box>
                          )}

                          <Box
                            component="span"
                            sx={{
                              backgroundColor:
                                colorByStatus[job.status] || "grey.100",
                              color: "white",
                              fontSize: "0.75rem",
                              py: 0.3,
                              px: 0.8,
                              borderRadius: 1,
                              display: "flex",
                              alignItems: "center",
                              fontWeight: 500,
                            }}
                          >
                            {statusToLabel(job.status)}
                          </Box>
                        </Box>

                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            gap: 1,
                          }}
                        >
                          <Box
                            sx={{
                              flex: 3,
                            }}
                          >
                            {sectionsWithContent.map(
                              (section, idx) =>
                                section.content.length > 0 && (
                                  <Box key={idx} sx={{ mb: 1 }}>
                                    <Typography
                                      variant="caption"
                                      sx={{
                                        mb: 0.25,
                                        fontWeight: "bold",
                                        display: "block",
                                      }}
                                    >
                                      {section.label}
                                    </Typography>
                                    <Paper
                                      variant="outlined"
                                      sx={{
                                        p: 0.5,
                                        backgroundColor: "grey.50",
                                      }}
                                    >
                                      {section.content.map(
                                        (phrase, phraseIdx) => (
                                          <Typography
                                            key={phraseIdx}
                                            variant="caption"
                                            sx={{
                                              whiteSpace: "pre-wrap",
                                              display: "block",
                                              py: 0.25,
                                              borderBottom:
                                                phraseIdx <
                                                section.content.length - 1
                                                  ? "1px solid"
                                                  : "none",
                                              borderColor: "divider",
                                            }}
                                          >
                                            <FormattedText text={phrase.phrase} />
                                          </Typography>
                                        )
                                      )}
                                    </Paper>
                                  </Box>
                                )
                            )}
                          </Box>

                          <Box
                            sx={{
                              flex: 1,
                              display: "flex",
                              flexDirection: "column",
                            }}
                          >
                            {job.images && job.images.length > 0 ? (
                              <Box
                                sx={{
                                  display: "grid",
                                  gridTemplateColumns: "repeat(2, 1fr)",
                                  gap: 0.5,
                                  padding: 0.5,
                                  backgroundColor: "grey.50",
                                  borderRadius: 1,
                                }}
                              >
                                {job.images
                                  .slice(0, 4)
                                  .map((img: string, idx: number) => (
                                    <Box
                                      key={idx}
                                      sx={{
                                        position: "relative",
                                        aspectRatio: "1/1",
                                        borderRadius: 0.5,
                                        overflow: "hidden",
                                        backgroundColor: "white",
                                      }}
                                    >
                                      <Box
                                        component="img"
                                        src={img}
                                        sx={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "contain",
                                        }}
                                        alt={`Product ${idx + 1}`}
                                      />
                                    </Box>
                                  ))}
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  height: "80px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  backgroundColor: "grey.100",
                                  borderRadius: 1,
                                }}
                              >
                                <Image color="disabled" sx={{ fontSize: 32 }} />
                              </Box>
                            )}
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Link>
                </Grid2>
              );
            })}
          </Grid2>
        </>
      )}
    </Box>
  );
}
